import { useEffect, useMemo, type ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';

import { useActiveThemeQuery } from '@/services/api/contentApi';

import { createSiteTheme, DEFAULT_THEME_COLORS } from './theme';

/**
 * Applies whichever theme an administrator has activated, live.
 *
 * <p>Renders with the built-in default colors until the active theme has
 * loaded — the same colors the backend seeds its "Default" theme with, so a
 * cold load never shows an arbitrary placeholder palette. Once the query
 * resolves, this re-renders with the live theme; server-rendered/prerendered
 * pages briefly show the last-deployed colors until that happens, which is
 * the accepted trade-off for changing the site's look without a rebuild.
 */
export function DynamicThemeProvider({ children }: { children: ReactNode }) {
  const { data: colors } = useActiveThemeQuery();

  const theme = useMemo(
    () => createSiteTheme(colors ?? DEFAULT_THEME_COLORS),
    [colors],
  );

  // Mobile browser chrome (the address bar tint) is native UI, not part of
  // the DOM the theme otherwise reaches — nudged separately so it tracks a
  // live color change too.
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    meta?.setAttribute('content', theme.palette.primary.main);
  }, [theme]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

export default DynamicThemeProvider;
